'use-strict';

import { showErrorModal } from 'Root/modal';

export default class PostcodeCheckHelpers {
    /**
     * @function stripLeadingZero
     * @param {Event} event - InputEvent
     * @description Strips the leading zero and disallows zero as a value.
     */
    static stripLeadingZero(event) {
        const { target: field, data } = event;

        if (data === '0') {
            const value = parseInt(field.value, 10);
            field.value = value > 0 ? value : '';
        }
    }

    /**
     * @function preventDashDotComma
     * @param {KeyboardEvent} event - keydown
     * @description Prevents a user from filling in a dash, dot or a comma.
     */
    static preventDashDotComma(event) {
        const { key } = event;
        if (['-', ',', '.'].includes(key)) {
            event.preventDefault();
        }
    }

    /**
     * @function respectMaxLength
     * @param {Event} event - Input event
     * @description input[type="number"] doesn't respect max-length attribute.
     * This method slices of the last character when exceeding the max length.
     */
    static respectMaxLength(event) {
        const { target } = event;
        if (target.value.length > target.maxLength) {
            target.value = target.value.slice(0, target.maxLength);
        }
    }

    /**
     * @function removePAID
     * @description Will remove the PAID when the user has made a change to the address while in edit mode. This references the VZPostcodeCheck form.
     */
    static removePAID() {
        if (this.hiddenInputWithPaid) this.hiddenInputWithPaid.remove();
    }

    /**
     * @function hideForm
     * @description Hides the location search form. This references the VZPostcodeCheck form.
     */
    static hideForm() {
        this.classList.add('d-none');
    }

    /**
     * @function showForm
     * @description Shows the location search form. This references the VZPostcodeCheck form.
     */
    static showForm() {
        this.classList.remove('d-none');
    }

    /**
     * @function handleErrors
     * @description handles network errors with a generic modal. This references the VZPostcodeCheck form.
     * @param {Error} error - The error thrown by fn fetchAddressAndAvailability is the response is not OK.
     */
    static handleErrors(error) {
        if (error) console.warn(error);
        const { title, body, cta } = JSON.parse(this.dataset?.generalError);
        showErrorModal(title, body, cta);
    }

    /**
     * @async
     * @function fetchAddressAndAvailability
     * @description Fetches either the address check or the availability endpoint based on waht's passed in.
     * @param {string} endpoint - A string representing the endpoint to fetch.
     * @param {HTMLFormElement} form - The availability check (VZPostcodeCheck) form.
     * @returns {Object} A Json object returned from the endpoint.
     */
    static async fetchAddressAndAvailability(endpoint, form) {
        const formData = new FormData(form);
        formData.append(window.csrf.tokenName, window.csrf.token);

        // This is not available in response.isml, so sending it to the BE to make it available there.
        formData.append('configuratorType', form.configuratorType);

        const fetchOptions = { method: 'POST', body: formData };
        const response = await fetch(endpoint, fetchOptions);

        if (!response.ok) {
            throw new Error(`There was an error fetching the endpoint - ${endpoint} - status: ${response.status}`);
        }

        const responseJson = await response.json();
        return responseJson;
    }

    /**
     * @function removeExistingFeedbackMessage
     * @description Removes the existing feedback messages if they exist.
     * @param {HTMLFormElement} form - The availability check (VZPostcodeCheck) form.
     * @param {string} classNames - A comma separated string of class selectors.
     */
    static removeExistingFeedbackMessage(form, classNames) {
        const existingMessages = [...form.querySelectorAll(classNames)];
        if (existingMessages) existingMessages.forEach((message) => message.remove());
    }

    /**
     * @function showFeedbackMessage
     * @description Shows the message by creating an element with the message and injects it into the VZPostcodeCheck form.
     * This references the VZPostcodeCheck form.
     * @param {string} message - The message to show in the form.
     * @param {boolean} isError - Determines whether the message passed in is an error message or not.
     */
    static showFeedbackMessage(message, isError = true) {
        const templateString = `<div class="js-availability-address-check-message">
                                    <p class="${isError ? 'text-red' : 'text-gray-800'} mb-0">${message}</p>
                                </div>`;
        const domParser = new DOMParser();
        const messageElement = domParser.parseFromString(templateString, 'text/html').querySelector('.js-availability-address-check-message');

        PostcodeCheckHelpers.removeExistingFeedbackMessage(this, '.js-availability-address-check-message');
        this.appendChild(messageElement);
    }

    /**
     * @function handleUnableToOrder
     * @description Updates availability check response heading with the address from the address check when a given location has no availability.
     * @returns {void} Early exit if the element is not found.
     */
    static handleUnableToOrder() {
        const responseHeadingElement = this.parentElement.querySelector('.js-availability-response-address');
        responseHeadingElement.setAttribute('unabletoorder', true);
        if (!responseHeadingElement) return;

        // Get room information if available
        const roomSelectElement = document.querySelector('.js-postcode-check-room');
        const roomValue = roomSelectElement ? roomSelectElement.value : null;

        // Set the address text, including room if present
        if (roomValue) {
            const roomLabel = responseHeadingElement.dataset.roomLabel;
            responseHeadingElement.innerHTML = `<span class="d-block">${this.addressCheckAddress}</span><span class="d-block">${roomLabel} ${roomValue}</span>`;
        } else {
            responseHeadingElement.innerHTML = `<span class="d-block">${this.addressCheckAddress}</span>`;
        }
    }

    /**
     * @function pushErrorsToDataLayer
     * @description When there is an error during the availability check, this will push an event notifying tag manager of the error.
     * @param {Object} addressParts - An object containing address details including the PAID.
     * @param {boolean} locationAlreadyInBasket - Whether the location is in the basket or not.
     */
    static pushErrorsToDataLayer(addressParts, locationAlreadyInBasket) {
        // Not a real error, so locationAlreadyInBasket errors won't be pushed.
        if (locationAlreadyInBasket) return;

        window.dataLayer = window.dataLayer || [];
        const { postalCode, houseNumber, extension, paid } = addressParts;

        // FYI - using addressCheckAddressParts from AddressCheck instead of address in the response of the availability check because that address is sometimes undefined.
        const erroneousAddressCombination = `${postalCode}-${houseNumber}${extension ? `-${extension}` : ''}`;
        const elementToPush = {
            event: 'availabilityCheckError', // eslint-disable-line quote-props
            payload: {
                // eslint-disable-line quote-props
                erroneousAddressCombination: erroneousAddressCombination.replace(/\s/g, ''),
                paid: paid || 'No PAID available for this address combination.'
            }
        };

        window.dataLayer.push(elementToPush);
    }
}
